/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2024 - All Rights Reserved
###################################################################
*/

/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable no-unused-expressions */

import * as yup from 'yup';
import { cloneDeep } from 'lodash';

import { 
  ContactsSection,
  EntityInformationSection,
  HierarchySection,
  TeamsSection
} from '../../index';

const groupMultipleFieldQuestions = (mainIdQuestion, questionnaire, operation, isBound) => {
  const groupQuestions = [];

  const mainQuestions = questionnaire.filter(question => question.idQuestion === mainIdQuestion);
  const mainQuestionIndex = questionnaire.findIndex(question => question.idQuestion === mainIdQuestion);

  for (const mainQuestion of mainQuestions) {
    let group = []

    if (mainQuestionIndex || mainQuestionIndex == 0) group.push(questionnaire[mainQuestionIndex]);

    if (mainQuestionIndex != -1) questionnaire.splice(mainQuestionIndex, 1);
    
    if (mainQuestion && mainQuestion.childrenQuestions && mainQuestion.childrenQuestions.length > 0) {
      mainQuestion.childrenQuestions.map(childQuestion => {
        group.push(childQuestion)
      });
    }

    group.sort((a, b) => a.sequenceQuestion - b.sequenceQuestion);
    groupQuestions.push(group.map((question, index) => ({ ...question, disabled: getQuestionIsDisabled(question, isBound) })));
  }

  return groupQuestions;
}

const getQuestionIsDisabled = (question, isBound) => {
  if (isBound) return true;
  return false;
}

function createSectionRow({
  questions,
  removeExtraDescriptionTop = false
}) {
  questions = JSON.parse(JSON.stringify(questions));
  return questions.map(question => {
    const { defaultValue } = question.behaviorObject || {};
    question.answer = defaultValue || "";
    question.answerJson = defaultValue || "";
    question.idAnswerOption = null;
    question.idPerson = null;
    question.idQuestionnaireAnswers = null;
    question.idQuestionnaireAnswersParent = null;
    question.idQuestionnaireAnswersRoot = null;
    question.key = null;
    question.disabled = false;

    if (question.behaviorObject?.extraExclamationWarning) {
      question.behaviorObject.extraExclamationWarning = false;
    }

    if (removeExtraDescriptionTop && question.behaviorObject?.extraDescriptionTop) {
      question.behaviorObject.extraDescriptionTop = '';
    }

    if (question?.idPerson) {
      question.idPerson = null
    } else if (question?.idPersonRelationship) {
      question.idPersonRelationship = null;
    }

    return question;
  })
}

const handleQuestionnaire = ({data, setQuestionnaire, setHierarchyGroup, setTeamsGroup, setFieldValue, setContactsGroup, personData, teams}) => {
  let questionnaire = [
    ...data.persons,
    ...data.operations,
    ...data.risks,
  ].sort((a, b) => a.sequenceQuestion - b.sequenceQuestion);

  setHierarchyGroup(HierarchySection.events.handleInitialAdjustments({
    questionnaire,
    setFieldValue,
    personData
  }));

  EntityInformationSection.events.handleInitialAdjustments({
    questionnaire,
    setFieldValue,
    personData
  });
  
  setTeamsGroup(TeamsSection.events.handleInitialAdjustments({
    questionnaire,
    teams
  }));

  setContactsGroup(ContactsSection.events.handleInitialAdjustments({
    questionnaire,
    teams,
    setFieldValue
  }));
  
  setQuestionnaire(questionnaire);
}

function handleSave({
  values,
  questionnaire,
  SECTIONS,
  mc,
  formikRef,
  refQuestionnaireSent,
  submitFunction,
  teamsGroup,
}) {

  buildValidation({
    values,
    questionnaire,
    SECTIONS,
    refQuestionnaireSent,
    teamsGroup,
  }).validate(values, { abortEarly: false }).then( () => {
    const isValidSubmittedForm = handleValidationsSubmittedForm({
      values,
      questionnaire,
      teamsGroup,
      setFieldError: formikRef.current.setFieldError,
      mc,
    });

    if (isValidSubmittedForm) {
      submitFunction()
    }

  }).catch((error) => {
    onValidationError({
      error,
      mc,
      formikRef,
      questionnaire,
      groupQuestionnaires: [
        ...teamsGroup,
      ],
    })
  })
}

function buildValidation({
  values,
  questionnaire,
  SECTIONS,
  teamsGroup,
  refQuestionnaireSent,
}) {
  const validationSchema = {};

  const validationQuestionnaire = handleQuestionnaireAnswerToSave({
    values,
    questionnaire,
    teamsGroup,
    onlyQuestions: true,
    SECTIONS,
  });

  let allQuestions = [...validationQuestionnaire];
  for (const question of allQuestions) {
    if (question.childrenQuestions?.length) allQuestions = [...allQuestions, ...question.childrenQuestions]
  }

  for (const question of allQuestions) {
    if ([614].includes(question?.idDomainQuestion)) continue;
    if (!question?.key) continue;

    question?.childrenQuestions?.map(item => {
      if (item.visibleQuestion && item.fieldMask?.type == 'phone' && item.behaviorObject?.isRequired) {
        validationSchema[item.key] = yup.string().test({
          name: "answer-validation",
          message: "Invalid phone",
          test: (value) => !!value && value.length >= 10
        })
      } else if (item.visibleQuestion && item.behaviorObject?.fieldMask == 'email') {
        if (item.answer) {
          validationSchema[item.key] = yup.string().email('Invalid email').required("Required field")
        } else {
          if (item.behaviorObject?.isRequired) {
            validationSchema[item.key] = yup.string().email("Invalid email").required("Required field")
          } else {
            validationSchema[item.key] = yup.string().notRequired();
          }
        }
      } else {
        validationSchema[item.key] = item.visibleQuestion && item.behaviorObject?.isRequired
          ? yup.string().test("answer-validation", "Required field", function (value) {
            return !!item.answer;
          }) : yup.string().notRequired();
      }
    })

    if (question.visibleQuestion && question.behaviorObject?.isRequired && question.fieldMask?.type == 'phone') {
      validationSchema[question.key] = yup.string().test({
        name: "answer-validation",
        message: "Invalid phone",
        test: (value) => !!value && value.length >= 10
      })
    } else if (question.visibleQuestion && question.behaviorObject?.fieldMask == 'email') {
      if (question.answer) {
        validationSchema[question.key] = yup.string().email("Invalid email").required("Required field")
      } else {
        if (question.behaviorObject?.isRequired) {
          validationSchema[question.key] = yup.string().email("Invalid email").required("Required field")
        } else {
          validationSchema[question.key] = yup.string().notRequired();
        }
      }
    } else {
      validationSchema[question.key] = question.visibleQuestion && question.behaviorObject?.isRequired
        ? yup.string().test("answer-validation", "Required field", function (value) {
          return !!question.answer;
        }) : yup.string().notRequired();
    }
  }

  return yup.object().shape(validationSchema);
}

function handleValidationsSubmittedForm({
  values,
  questionnaire,
  setFieldError,
  mc,
  teamsGroup,
}) {

  let validationResult = true;

  let firstErrorKey = null;

  if (firstErrorKey) {
    const elements = global.window.document.getElementsByName(firstErrorKey);
    if (elements?.length) {
      setTimeout(() => elements[0].scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' }), 300);
    }
  }

  return validationResult;
}

function onValidationError({
  error,
  mc,
  formikRef,
  questionnaire,
  groupQuestionnaires
}) {
  console.log('onValidationError catch initial', error)

  questionnaire = cloneDeep(questionnaire);
  groupQuestionnaires = cloneDeep(groupQuestionnaires);

  mc.error('Please complete all required fields to proceed.')
  const allQuestions = questionnaire;

  groupQuestionnaires.forEach((grpQuestionnaire) => {
    Array.isArray(grpQuestionnaire)
      ? grpQuestionnaire.map(question => allQuestions.push(question))
      : grpQuestionnaire.questions.map(question => allQuestions.push(question))
  });

  let firstErrorKey = null;
  try {
    for (const question of allQuestions) {
      if (!formikRef.current.errors || !formikRef.current.errors[question.key]) continue;
      formikRef.current.setFieldError(question.key, null);
    }

    error.inner.map(({ message, path }) => {
      const question = allQuestions.find(question => question.key === path);

      if (question.dsQuestion === "E-mail") {
        mc.error(`The email entered is invalid`);
        formikRef.current.setFieldError(question.key, `* Invalid email`);
      } else {
        mc.error(`The Field: ${question.dsQuestion} is required`);
        formikRef.current.setFieldError(question.key, `* Required field`);
      }

      if (!firstErrorKey) firstErrorKey = question.key;
    });

    if (firstErrorKey) {
      const elements = global.window.document.getElementsByName(firstErrorKey);
      if (elements?.length) {
        setTimeout(() => elements[0].scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' }), 300);
      }
    }
  } catch (e) {
    console.log('onValidationError catch', e)
  }
}

function handleQuestionnaireAnswerToSave({
  values,
  questionnaire,
  onlyQuestions,
  SECTIONS,
  teamsGroup,
}) {
  const formValues = JSON.parse(JSON.stringify(values)) || {};

  let questionnaireAnswers = []

  for (const [, section] of Object.entries(SECTIONS)) {
    if (section.isMultiple) {
      let multipleFields = []
      switch (section.name) {
        case 'TEAMS':
          multipleFields = cloneDeep(teamsGroup);
          break;
        default:
          multipleFields = [];
          break;
      }

      multipleFields.map((field, index) => {
        let sectionAnswers = formValues[`$ANSWERS_${section.name}_${index}`];
        if (sectionAnswers?.length && field.some(({ answer }) => answer)) {
          const rootQuestion = sectionAnswers.find((question) => !question.idQuestionRoot);
          const childrenQuestions = sectionAnswers.filter((question) => question.idQuestionRoot);
          rootQuestion.childrenQuestions = childrenQuestions;
          if (!onlyQuestions) {
            questionnaireAnswers = [...questionnaireAnswers, rootQuestion];
          } else {
            questionnaireAnswers = [...questionnaireAnswers, ...sectionAnswers];
          }
        }
      });
    } else {
      let sectionAnswers = formValues[`$ANSWERS_${section.name}`];
      if (sectionAnswers) {
        questionnaireAnswers = [...questionnaireAnswers, ...sectionAnswers];
      }
    }
  }

  let childrenQuestionIds = [];
  for (const question of questionnaireAnswers) {
    if (question) {
      if (
        (question.idDomainQuestion === 270 ||
          question.idDomainQuestion === 271 ||
          (question.idDomainQuestion === 44 && typeof question.answer === 'object')
        ) && question.answer
      ) {
        try {
          question.answer = JSON.stringify(question.answer)
        } catch (e) {
        }
      }

      if (SECTIONS[question.behaviorObject?.sectionName] && SECTIONS[question.behaviorObject?.sectionName].isMultiple) {
        if (question?.childrenQuestions?.length) {
          const idQuestions = question.childrenQuestions.map(question => question.idQuestion);
          childrenQuestionIds = [...childrenQuestionIds, ...idQuestions];
        }
      } else {
        question.childrenQuestions = [];
      }
    }
  }

  questionnaireAnswers = questionnaireAnswers.filter(v => !childrenQuestionIds.includes(v.idQuestion))

  return questionnaireAnswers;
}

const events = {
  groupMultipleFieldQuestions,
  createSectionRow,
  handleQuestionnaire,
  handleSave,
};

export { events };